import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { requestPasswordReset, resetPassword } from '../api/client';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Layout } from '../components/Layout';
import { colors } from '../constants/colors';

function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function getDigitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, 10);
}

type Step = 'phone' | 'reset' | 'success';

export function ForgotPasswordPage() {
  useDocumentTitle('Reset Password');
  const [step, setStep] = useState<Step>('phone');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fullPhoneNumber = `+1${phoneDigits}`;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneDigits(getDigitsOnly(e.target.value));
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (phoneDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setIsLoading(true);
    try {
      await requestPasswordReset(fullPhoneNumber);
      setStep('reset');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (code.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(fullPhoneNumber, code, newPassword);
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Reset Password</h1>

        {step === 'phone' && (
          <>
            <p style={styles.subtitle}>
              Enter your phone number and we'll send you a code to reset your password.
            </p>
            <form onSubmit={handleSendCode} style={styles.form}>
              {error && <div style={styles.error}>{error}</div>}

              <div style={styles.field}>
                <label style={styles.label}>Phone Number</label>
                <div style={styles.phoneContainer}>
                  <div style={styles.countryCode}>+1</div>
                  <input
                    type="tel"
                    value={formatPhoneNumber(phoneDigits)}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    style={styles.phoneInput}
                    required
                  />
                </div>
              </div>

              <button type="submit" style={styles.button} disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>
          </>
        )}

        {step === 'reset' && (
          <>
            <p style={styles.subtitle}>
              Enter the 6-digit code sent to your phone and choose a new password.
            </p>
            <form onSubmit={handleResetPassword} style={styles.form}>
              {error && <div style={styles.error}>{error}</div>}

              <div style={styles.field}>
                <label style={styles.label}>Reset Code</label>
                <input
                  type="text"
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  style={{ ...styles.input, textAlign: 'center', letterSpacing: '8px', fontSize: '24px' }}
                  maxLength={6}
                  inputMode="numeric"
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  style={styles.input}
                  minLength={8}
                  required
                />
              </div>

              <button type="submit" style={styles.button} disabled={isLoading}>
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}

        {step === 'success' && (
          <>
            <div style={styles.successIcon}>&#10003;</div>
            <p style={styles.successText}>
              Your password has been reset successfully.
            </p>
            <Link to="/" style={styles.successButton}>
              Back to Sign In
            </Link>
          </>
        )}

        {step !== 'success' && (
          <p style={styles.footer}>
            Remember your password?{' '}
            <Link to="/" style={styles.link}>
              Sign in
            </Link>
          </p>
        )}
      </div>
    </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: 'calc(100vh - 200px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '400px',
    border: `1px solid ${colors.gray[300]}`,
  },
  title: {
    color: colors.ink.black,
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '8px',
    textAlign: 'center',
  },
  subtitle: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '32px',
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    color: '#333',
    fontSize: '14px',
    fontWeight: '600',
  },
  input: {
    backgroundColor: colors.white,
    border: '1px solid #d0d0d0',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '16px',
    color: colors.ink.black,
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  phoneContainer: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '0',
  },
  countryCode: {
    backgroundColor: colors.gray[100],
    border: '1px solid #d0d0d0',
    borderRight: 'none',
    borderRadius: '8px 0 0 8px',
    padding: '12px 14px',
    fontSize: '16px',
    color: '#666',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
  },
  phoneInput: {
    flex: 1,
    backgroundColor: colors.white,
    border: '1px solid #d0d0d0',
    borderRadius: '0 8px 8px 0',
    padding: '12px 16px',
    fontSize: '16px',
    color: colors.ink.black,
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  button: {
    backgroundColor: '#5b21b6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '12px',
    transition: 'background-color 0.2s',
  },
  error: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '12px',
    color: '#dc2626',
    fontSize: '14px',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    fontSize: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '24px auto',
  },
  successText: {
    color: '#333',
    fontSize: '16px',
    textAlign: 'center',
    marginBottom: '24px',
  },
  successButton: {
    display: 'block',
    backgroundColor: '#5b21b6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: 'none',
    transition: 'background-color 0.2s',
  },
  footer: {
    color: '#666',
    fontSize: '14px',
    textAlign: 'center',
    marginTop: '24px',
  },
  link: {
    color: '#5b21b6',
    textDecoration: 'none',
    fontWeight: '500',
  },
};
